import { AxiosResponse } from 'axios';
import {
  SET_EMPLOYEE_REPORTS,
  SET_PAYROLL_REPORTS,
  SET_PROJECT_REPORTS,
  SET_REPORT_FACETS,
  SET_REPORT_RESULT,
  SET_REPORT_EXPORT,
  SET_ERRORMSG,
} from '@/store/mutation_types';
import {
  GET_EMPLOYEE_REPORTS,
  GET_PAYROLL_REPORTS,
  GET_PROJECT_REPORTS,
  GET_REPORT_FACETS,
  GENERATE_REPORT,
  EXPORT_REPORT,
  CLEAR_REPORT_RESULT,
} from '@/store/action_types';
import Reports from '@/services/reports';
import { Module } from 'vuex';
import { GenerateReportParams, ReportFacetsParams, ReportsParams } from '@/dtos/reports.dto';

const simpleFetch = async (commit: any, mutation: string, request: () => Promise<AxiosResponse>) => {
  commit(mutation, { data: null, isLoading: true });
  const result: AxiosResponse = await request();
  if (result && result.data?.status) {
    commit(mutation, { data: result.data.data, isLoading: false });
    return true;
  }
  commit(mutation, { data: null, isLoading: false });
  if (result?.data?.msg) commit(SET_ERRORMSG, result.data.msg);
  return false;
};

const reports: Module<any, {}> = {
  state: {
    employeeReports: { data: null, isLoading: false },
    payrollReports: { data: null, isLoading: false },
    projectReports: { data: null, isLoading: false },
    reportFacets: { data: null, isLoading: false },
    reportResult: { data: null, isLoading: false, error: null },
    reportExport: { isLoading: false },
  },
  getters: {
    employeeReports: (state: { employeeReports: any }) => state.employeeReports,
    payrollReports: (state: { payrollReports: any }) => state.payrollReports,
    projectReports: (state: { projectReports: any }) => state.projectReports,
    reportFacets: (state: { reportFacets: any }) => state.reportFacets,
    reportResult: (state: { reportResult: any }) => state.reportResult,
    reportExport: (state: { reportExport: any }) => state.reportExport,
  },
  mutations: {
    [SET_EMPLOYEE_REPORTS]: (state: { employeeReports: any }, payload: { data: any; isLoading: boolean }) => {
      state.employeeReports.data = payload.data;
      state.employeeReports.isLoading = payload.isLoading;
    },
    [SET_PAYROLL_REPORTS]: (state: { payrollReports: any }, payload: { data: any; isLoading: boolean }) => {
      state.payrollReports.data = payload.data;
      state.payrollReports.isLoading = payload.isLoading;
    },
    [SET_PROJECT_REPORTS]: (state: { projectReports: any }, payload: { data: any; isLoading: boolean }) => {
      state.projectReports.data = payload.data;
      state.projectReports.isLoading = payload.isLoading;
    },
    [SET_REPORT_FACETS]: (state: { reportFacets: any }, payload: { data?: any; isLoading: boolean }) => {
      if (payload.data !== undefined) state.reportFacets.data = payload.data;
      state.reportFacets.isLoading = payload.isLoading;
    },
    [SET_REPORT_RESULT]: (state: { reportResult: any }, payload: { data?: any; isLoading: boolean; error?: string | null }) => {
      if (payload.data !== undefined) state.reportResult.data = payload.data;
      if (payload.error !== undefined) state.reportResult.error = payload.error;
      state.reportResult.isLoading = payload.isLoading;
    },
    [SET_REPORT_EXPORT]: (state: { reportExport: any }, payload: { isLoading: boolean }) => {
      state.reportExport.isLoading = payload.isLoading;
    },
  },
  actions: {
    [GET_EMPLOYEE_REPORTS]: ({ commit }: any, payload: ReportsParams) => simpleFetch(commit, SET_EMPLOYEE_REPORTS, () => Reports.getEmployeeReports(payload)),
    [GET_PAYROLL_REPORTS]: ({ commit }: any, payload: ReportsParams) => simpleFetch(commit, SET_PAYROLL_REPORTS, () => Reports.getPayrollReports(payload)),
    [GET_PROJECT_REPORTS]: ({ commit }: any, payload: ReportsParams) => simpleFetch(commit, SET_PROJECT_REPORTS, () => Reports.getProjectReports(payload)),
    [GET_REPORT_FACETS]: async ({ commit, state }: any, payload: ReportFacetsParams & { force?: boolean }) => {
      if (state.reportFacets.data && !payload.force) return true;
      commit(SET_REPORT_FACETS, { isLoading: true });
      const result: AxiosResponse = await Reports.getReportFacets({ organizationId: payload.organizationId });
      if (result && result.data?.status) {
        commit(SET_REPORT_FACETS, { data: result.data.data, isLoading: false });
        return true;
      }
      commit(SET_REPORT_FACETS, { isLoading: false });
      if (result?.data?.msg) commit(SET_ERRORMSG, result.data.msg);
      return false;
    },
    [GENERATE_REPORT]: async ({ commit }: any, payload: GenerateReportParams) => {
      commit(SET_REPORT_RESULT, { isLoading: true, error: null });
      try {
        const result: AxiosResponse = await Reports.generateReport(payload);
        if (result && result.data?.status) {
          commit(SET_REPORT_RESULT, { data: result.data.data, isLoading: false, error: null });
          return result.data.data;
        }
        const message = result?.data?.msg || result?.data?.message || 'The report could not be generated.';
        commit(SET_REPORT_RESULT, { isLoading: false, error: message });
        return false;
      } catch (error: any) {
        commit(SET_REPORT_RESULT, { isLoading: false, error: error?.response?.data?.message || 'The report could not be generated.' });
        return false;
      }
    },
    [EXPORT_REPORT]: async ({ commit }: any, payload: GenerateReportParams) => {
      commit(SET_REPORT_EXPORT, { isLoading: true });
      try {
        const result: AxiosResponse = await Reports.exportReport(payload);
        return result || false;
      } catch {
        return false;
      } finally {
        commit(SET_REPORT_EXPORT, { isLoading: false });
      }
    },
    [CLEAR_REPORT_RESULT]: ({ commit }: any) => {
      commit(SET_REPORT_RESULT, { data: null, isLoading: false, error: null });
    },
  },
};

export default reports;
